import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Home, Sparkles, DoorOpen, Package, Wind, Building2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";

// Visual metadata per checklist category. Textual content is fully i18n-driven.
const CATEGORY_META = [
  { key: "regular", icon: Home, color: "from-[#5BA83F] to-[#3F7A1F]" },
  { key: "deep", icon: Sparkles, color: "from-[#1B3A6B] to-[#152D54]" },
  { key: "movein", icon: DoorOpen, color: "from-[#5BA83F] to-[#3F7A1F]" },
  { key: "post", icon: Package, color: "from-[#1B3A6B] to-[#152D54]" },
  { key: "office", icon: Building2, color: "from-[#5BA83F] to-[#3F7A1F]" },
  { key: "windows", icon: Wind, color: "from-[#1B3A6B] to-[#152D54]" },
];

const ADDON_KEYS = ["fridge", "oven", "cabinets", "radiator"];

export default function ChecklistPage() {
  const { t } = useTranslation();
  const [active, setActive] = useState("regular");
  const section = CATEGORY_META.find((s) => s.key === active) || CATEGORY_META[0];
  const sectionData = t(`checklist_page_v2.categories.${section.key}`, { returnObjects: true }) || { title: "", subtitle: "", rooms: [] };
  const addons = t("checklist_page_v2.addons_items", { returnObjects: true }) || {};

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#1B3A6B] via-[#22497F] to-[#1B3A6B] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-bold text-[#9BD478]">
            <CheckCircle2 className="w-4 h-4" /> {t("checklist_page_v2.hero_eyebrow")}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-4 leading-tight">
            {t("checklist_page_v2.hero_title")}
          </h1>
          <p className="text-slate-200 mt-4 max-w-2xl text-base md:text-lg">
            {t("checklist_page_v2.hero_subtitle")}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10 md:py-14 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Sidebar navigation */}
        <aside>
          <div className="lg:sticky lg:top-24 space-y-2" data-testid="checklist-nav">
            {CATEGORY_META.map((s) => {
              const Icon = s.icon;
              const isActive = s.key === active;
              const meta = t(`checklist_page_v2.categories.${s.key}`, { returnObjects: true }) || {};
              return (
                <button
                  key={s.key}
                  onClick={() => setActive(s.key)}
                  className={`w-full text-left rounded-xl px-4 py-3 flex items-start gap-3 transition ${
                    isActive
                      ? "bg-white ring-2 ring-[#5BA83F] shadow-sm"
                      : "bg-white/60 hover:bg-white ring-1 ring-slate-200"
                  }`}
                  data-testid={`checklist-nav-${s.key}`}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white bg-gradient-to-br ${s.color} shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <div className={`font-semibold text-sm ${isActive ? "text-[#1B3A6B]" : "text-slate-800"}`}>{meta.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{meta.subtitle}</div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Section content */}
        <section data-testid={`checklist-section-${section.key}`}>
          <div className={`rounded-2xl p-6 md:p-8 text-white bg-gradient-to-br ${section.color}`}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center">
                <section.icon className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold">{sectionData.title}</h2>
                <p className="text-white/85 text-sm mt-1">{sectionData.subtitle}</p>
              </div>
            </div>
          </div>

          <div className="mt-6 grid md:grid-cols-2 gap-4">
            {(sectionData.rooms || []).map((r) => (
              <div key={r.room} className="bg-white rounded-xl ring-1 ring-slate-200 p-5">
                <h3 className="font-heading font-bold text-[#1B3A6B] text-lg">{r.room}</h3>
                <ul className="mt-3 space-y-2">
                  {(r.items || []).map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[#5BA83F] mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Add-ons callout (regular + deep) */}
          {(section.key === "regular" || section.key === "deep") && (
            <div className="mt-6 bg-white rounded-xl ring-1 ring-slate-200 p-5" data-testid="checklist-addons">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wide font-bold text-[#5BA83F]">
                <Sparkles className="w-4 h-4" /> {t("checklist_page_v2.addons_eyebrow")}
              </div>
              <h3 className="font-heading font-bold text-[#1B3A6B] text-lg mt-1.5">{t("checklist_page_v2.addons_title")}</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {ADDON_KEYS.map((k) => {
                  const a = addons[k] || { label: k, minutes: 0 };
                  return (
                    <div key={k} className="rounded-lg bg-[#E8F5E0] ring-1 ring-[#5BA83F]/30 px-3 py-3">
                      <div className="font-semibold text-[#1F4D0F] text-sm">{a.label}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{t("checklist_page_v2.unit_minutes", { n: a.minutes })}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 flex items-start gap-3 bg-amber-50 ring-1 ring-amber-200 rounded-xl p-4 text-sm text-amber-900" data-testid="checklist-disclaimer">
            <Info className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">{t("checklist_page_v2.disclaimer_title")}</div>
              <p className="mt-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("checklist_page_v2.disclaimer_body") }} />
            </div>
          </div>

          {/* CTA */}
          <div className="mt-8 rounded-2xl bg-[#0F172A] text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#9BD478]">{t("checklist_page_v2.cta_eyebrow")}</div>
              <div className="font-heading text-2xl font-bold mt-1">{t("checklist_page_v2.cta_title")}</div>
            </div>
            <div className="flex gap-2">
              <Button asChild className="bg-[#5BA83F] hover:bg-[#4A8A33] text-white rounded-full">
                <Link to="/book">{t("checklist_page_v2.cta_book")}</Link>
              </Button>
              <Button asChild variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full">
                <Link to="/services">{t("checklist_page_v2.cta_explore")}</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
